import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Money as MoneyType } from '../../types';
import { Money } from '../../Money';
import WarningModal from '../../WarningModal';
import { useTheme } from '../../theme';
import MediaQuery from '../../MediaQuery/MediaQuery';
import Tooltip from '@mui/material/Tooltip';
import { ArrowForward } from '../../Icons';
import ModalConfirm from '../../ModalConfirm';
import { BaseButton, IconButton } from '../../BaseComponents';
import { useElementHeightCSSVar } from '../../hooks';

export interface ToolbarProps {
	className?: string;
	innerClassName?: string;
	nextButtonClassName?: string;
	totalPrice: MoneyType;
	onButtonClick?: (event: React.SyntheticEvent) => void;
	onBackButtonClick?: (event: React.SyntheticEvent) => void;
	additional?: React.ReactNode;
	actionsRight?: boolean;
	hideNextButton?: boolean;
	buttonDisabled?: boolean;
	isLoading?: boolean;
	error?: Error;
	onCloseErrorMsg?: () => void;
	miles?: number;
	priceInMiles?: number;
	onRetrySearch?: () => void;
	nextButtonText?: string;
}

let lastErrorMessage = '';

const Toolbar: React.FunctionComponent<ToolbarProps> = ({
	totalPrice,
	onBackButtonClick,
	onButtonClick,
	additional,
	buttonDisabled,
	actionsRight,
	isLoading,
	error,
	hideNextButton,
	onCloseErrorMsg,
	miles,
	className,
	nextButtonClassName,
	priceInMiles,
	innerClassName,
	onRetrySearch,
	nextButtonText
}) => {
	const { Toolbar: css } = useTheme('Toolbar');
	const { t } = useTranslation('Toolbar');
	const { ref, initialHeight } = useElementHeightCSSVar('toolbar-height');

	// This prevents error message blinking.
	if (error && error.message) {
		lastErrorMessage = error.message;
	}

	const retryErrorMessages = ['No appropriate flights found on the segment'];

	const retryMessage = retryErrorMessages.find(it => error?.message?.includes(it));

	return (
		<div
			className={cn(css.toolbar, className, 'mui-fixed', { [css.toolbar_tall]: initialHeight > 400 })}
			id="toolbar"
			data-test-id="toolbar"
			ref={ref as React.MutableRefObject<HTMLDivElement>}
		>
			<WarningModal
				title={t('Oops...')}
				content={t(
					'An unexpected error has occurred during the creating order process. Please try again later or contact a customer support service.'
				)}
				isOpen={!!error && !retryMessage}
				onButtonClick={onCloseErrorMsg}
				errorMessage={lastErrorMessage}
				onClose={onCloseErrorMsg}
				buttonText={t('OK, thank you')}
			/>
			<ModalConfirm
				title={t('Error...')}
				isOpen={!!error && !!retryMessage}
				onConfirm={() => {
					onCloseErrorMsg();
					onRetrySearch();
				}}
				onClose={onCloseErrorMsg}
				onCancel={onCloseErrorMsg}
				confirmBtn={t('Try again')}
			>
				<div>{t(retryMessage)}</div>
			</ModalConfirm>
			<div className={cn(css.inner, innerClassName)}>
				<div className={cn(css.actions, css.actions_left)}>
					{!!onBackButtonClick && (
						<div
							className={css.backButton__wrapper}
							data-test-id="toolbar-back"
							onClick={onBackButtonClick}
						>
							<IconButton className={css.backButton}>{ArrowForward}</IconButton>
							<span className={css.backButton__text}>{t('Back')}</span>
						</div>
					)}

					{additional}
				</div>

				<div className={cn(css.actions, { [css.actions_right]: actionsRight })}>
					{totalPrice && !!totalPrice.amount && (
						<span className={css.price} data-test-id="total-price">
							<MediaQuery minWidth="mobile">
								{t('Total')}: <Money money={totalPrice} />
								{(miles > 0 || priceInMiles > 0) && (
									<div className={css.miles}>
										<Money
											money={{
												amount: priceInMiles > 0 ? priceInMiles : miles,
												currency: 'miles'
											}}
											showCurrency={false}
											moneyClassName={css.miles__money}
										/>
										{t(priceInMiles > 0 ? 'miles will be charged' : 'miles will be awared')}

										<Tooltip
											title={t(
												priceInMiles > 0
													? 'This flight will be partially paid by miles'
													: 'Fly with us and get miles'
											)}
										>
											<div className={css.miles__hint} />
										</Tooltip>
									</div>
								)}
							</MediaQuery>
						</span>
					)}

					{!hideNextButton && (
						<BaseButton
							className={cn(css.button, nextButtonClassName, { [css.button_disabled]: buttonDisabled })}
							onClick={e => {
								onButtonClick?.(e);
							}}
							variant="smallPrimary"
							isLoading={isLoading}
							disabled={buttonDisabled}
							type={onButtonClick ? 'button' : 'submit'}
							data-next-button={true}
						>
							{nextButtonText ?? t('Continue')}
						</BaseButton>
					)}
				</div>
			</div>
		</div>
	);
};

export default Toolbar;
